import { useTranslations } from 'next-intl';
import { type FC } from 'react';
import { Controller, FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import clsx from 'clsx';
import dayjs from 'dayjs';
import { z } from 'zod';

import { Checkbox } from '@/components/common/Checkbox';
import { FormDateInput } from '@/components/common/form/FormDateInput';
import { FormSelect } from '@/components/common/form/FormSelect';
import { FormSubmitButton } from '@/components/common/form/FormSubmitButton';
import { CurrencySelect } from '@/components/common/select/CurrencySelect';
import { OperationTypeSelect } from '@/components/common/select/OperationTypeSelect';
import { RevenueChartComparisonSelect } from '@/components/common/select/RevenueChartComparisonSelect';
import { type WithClassName } from '@/types/react';

import {
  DashboardV2ComparisonMode,
  RevenueChartOperationType,
} from '../../../../../../../../../../../../../generated/graphql';

import styles from './RevenueChartFilter.module.scss';

type RevenueChartFilterProps = WithClassName & {
  defaultValues?: Partial<RevenueChartFilter>;
  isInGate: boolean;
  onSubmit: SubmitHandler<RevenueChartFilter>;
};

const comparisonModeSchema = z.enum([DashboardV2ComparisonMode.PreviousPeriod, DashboardV2ComparisonMode.PreviousYear]);

const useFormSchema = () => {
  return z.object({
    dateFrom: z.date(),
    dateTo: z.date(),
    compareEnabled: z.boolean(),
    comparisonMode: comparisonModeSchema,
    currency: z.object({
      id: z.string(),
      name: z.string(),
    }),
    operationType: z.nativeEnum(RevenueChartOperationType),
  });
};

export type RevenueChartFilter = z.infer<ReturnType<typeof useFormSchema>>;

export const getDefaultRevenueChartFilterValues = (): RevenueChartFilter => ({
  dateFrom: dayjs().subtract(6, 'day').startOf('day').toDate(),
  dateTo: dayjs().endOf('day').toDate(),
  compareEnabled: true,
  comparisonMode: DashboardV2ComparisonMode.PreviousPeriod,
  currency: { id: 'USD', name: '' },
  operationType: RevenueChartOperationType.Invoice,
});

export const getRevenueChartComparisonMode = (filter: RevenueChartFilter): DashboardV2ComparisonMode => {
  if (!filter.compareEnabled) {
    return DashboardV2ComparisonMode.None;
  }

  return filter.comparisonMode;
};

export const RevenueChartFilter: FC<RevenueChartFilterProps> = (props) => {
  const t = useTranslations('charts.filters');
  const tButtons = useTranslations('buttons');

  const { className, defaultValues, isInGate, onSubmit } = props;

  const form = useForm<RevenueChartFilter>({
    resolver: zodResolver(useFormSchema()),
    defaultValues: {
      ...getDefaultRevenueChartFilterValues(),
      ...defaultValues,
    },
  });

  const { handleSubmit, register, control, watch } = form;
  const compareEnabled = watch('compareEnabled');

  return (
    <FormProvider {...form}>
      <form className={clsx(styles.root, className)} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.dateRow}>
          <FormDateInput {...register('dateFrom')} variant="box" label={t('dateFrom.label')} showTimeInput={false} />

          <FormDateInput {...register('dateTo')} variant="box" label={t('dateTo.label')} showTimeInput={false} />

          <Controller
            name="compareEnabled"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Checkbox
                className={styles.compareToggle}
                checked={value}
                label={t('comparison.label')}
                onChange={onChange}
              />
            )}
          />

          {compareEnabled && (
            <FormSelect
              {...register('comparisonMode')}
              variant="box"
              label={t('comparison.modeLabel')}
              component={RevenueChartComparisonSelect}
            />
          )}
        </div>

        <div className={clsx(styles.filtersRow, isInGate && styles.filtersRowInGate)}>
          {!isInGate && (
            <FormSelect
              {...register('currency')}
              variant="box"
              label={t('currency.label')}
              component={CurrencySelect}
            />
          )}

          <FormSelect
            {...register('operationType')}
            variant="box"
            label={t('operationType.label')}
            component={OperationTypeSelect}
          />

          <FormSubmitButton className={styles.submitButton}>{tButtons('search')}</FormSubmitButton>
        </div>
      </form>
    </FormProvider>
  );
};
